import type { Row } from "@rocicorp/zero";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { userDisplayName } from "../lib/userDisplayName";
import { Avatar } from "./Avatar";
import { FacePile } from "./FacePile";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

type Contributor = {
  user?: Row["user"] | null;
  lastMessagedAt?: number | null;
};

type Props = {
  contributors: readonly Contributor[];
  presentUserIds?: ReadonlySet<string>;
  collapseToFit?: boolean;
};

type TooltipContentProps = {
  name: string;
  isPresent: boolean;
  lastActivityTimestamp: number;
};

function TooltipContent({
  name,
  isPresent,
  lastActivityTimestamp,
}: TooltipContentProps) {
  const timeText = isPresent
    ? "Active now"
    : formatRelativeTime(lastActivityTimestamp);

  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      <Text variant="text-small-md">{name}</Text>
      <Text variant="text-small" color="var(--tooltip-fg-muted)">
        {timeText}
      </Text>
    </span>
  );
}

export function ActivityFacepile({
  contributors,
  presentUserIds,
  collapseToFit = false,
}: Props) {
  const users = contributors.flatMap(({ user, lastMessagedAt }) =>
    user && lastMessagedAt != null
      ? [
          {
            user,
            lastActivityTimestamp: lastMessagedAt,
          },
        ]
      : []
  );

  const uniqueUsers = users.filter(
    (item, index, arr) =>
      arr.findIndex((other) => other.user.id === item.user.id) === index
  );

  if (uniqueUsers.length === 0) return null;

  const size = 24;

  return (
    <FacePile
      itemSize={24}
      outlineWidth={2}
      cutout
      collapseToFit={collapseToFit}
      align="end"
      items={uniqueUsers.map(({ user, lastActivityTimestamp }) => {
        const isPresent = presentUserIds?.has(user.id) ?? false;
        const name = userDisplayName(user);

        return {
          node: (
            <Tooltip
              content={
                <TooltipContent
                  name={name}
                  isPresent={isPresent}
                  lastActivityTimestamp={lastActivityTimestamp}
                />
              }
            >
              <Avatar
                size={size}
                user={user}
                isPresent={isPresent}
                isMuted={!isPresent}
                bare={!isPresent}
              />
            </Tooltip>
          ),
          id: user.id,
          label: name,
          bare: !isPresent,
        };
      })}
    />
  );
}
